import { auth } from '@/lib/firebase.ts';
import { queryClient } from '@/App.tsx';
import { userApiQueryKeys } from '@/features/dashboard/_root/api/userApi.queryKeys.ts';
import { MyAccessDto } from '@/features/dashboard/_root/types/MyAccessDto.ts';
import { resolveCurrentCompanyId } from '@/features/dashboard/_root/access/currentCompany.ts';
import {
  MissingTenantError,
  UnauthenticatedError,
} from '@/lib/api/exception.ts';

// `GET /api/template-styles/document.css` returns `text/css` with a strong
// ETag — neither fits `fetchPrivateApi` (which is JSON-shaped and returns
// `null` for non-JSON), so this is a dedicated reader that reuses the same auth
// (Firebase bearer) + `X-Tenant-Id` headers and adds conditional-request /
// 304 handling. The CSS is tenant-independent, but the backend tenant filter
// still requires the header on every `/api/**` call.
const DOCUMENT_CSS_PATH = 'template-styles/document.css';

export interface DocumentCssResult {
  notModified: boolean;
  // present only when `notModified` is false
  css?: string;
  etag?: string | null;
}

export const fetchDocumentBaseCss = async (
  etag?: string | null,
): Promise<DocumentCssResult> => {
  await auth.authStateReady();
  const token = await auth?.currentUser?.getIdToken();
  if (!token) {
    throw new UnauthenticatedError();
  }

  const tenantId = resolveCurrentCompanyId(
    queryClient.getQueryData<MyAccessDto>(userApiQueryKeys.me()),
  );
  if (!tenantId) {
    throw new MissingTenantError();
  }

  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Id': tenantId,
  };
  if (etag) {
    headers['If-None-Match'] = etag;
  }

  const res = await fetch(
    `${import.meta.env.VITE_CAMPDESK_API_URL}${DOCUMENT_CSS_PATH}`,
    { method: 'GET', headers, cache: 'no-store' },
  );

  // Server confirms our cached copy is still current — caller keeps it.
  if (res.status === 304) {
    return { notModified: true };
  }
  if (!res.ok) {
    throw new Error(`Failed to load document base CSS: ${res.status}`);
  }

  const css = await res.text();
  return { notModified: false, css, etag: res.headers.get('ETag') };
};
